import { ColumnTable } from '@octano/global-ui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TranslationsKeys } from '../../../../locales/translations';
import { format } from '../../../../utils/percentage';
import { PerformanceAnalysisDetailTablePassed } from '../parts/PerformanceAnalysisDetailTablePassed';
import { PerformanceAnalysisDetailRow } from '../types';

export function useDetailColumns(): ColumnTable<PerformanceAnalysisDetailRow>[] {
  const { t } = useTranslation(
    TranslationsKeys.MANAGEMENT_TOOLS_PERFORMANCE_ANALYSIS,
  );

  const table = useMemo(() => {
    return t<
      string,
      {
        headers: {
          studentFullName: string;
          studentIdentification: string;
          studyPlanVersionName: string;
          finalPartialPercentage: string;
          partial: string;
          partialPassed: string;
          finalExamPercentage: string;
          exam: string;
          examPassed: string;
          final: string;
          finalPassed: string;
        };
        passedStatus: {
          PASSED: string;
          FAILED: string;
          PENDING: string;
        };
      }
    >('tableDetail', {
      returnObjects: true,
    });
  }, [t]);

  return [
    {
      columnName: 'studentFullName',
      headerText: table.headers.studentFullName,
      tdClassName: 'text-center',
      thClassName: 'text-center',
      cellFormat: ({ row }) => {
        return <>{row.studentFullName}</>;
      },
    },
    {
      columnName: 'studentIdentification',
      headerText: table.headers.studentIdentification,
      tdClassName: 'text-center',
      thClassName: 'text-center',
      cellFormat: ({ row }) => {
        return (
          <>
            {row.studentIdentificationTypeName}{' '}
            {row.studentIdentificationValueFormated}
          </>
        );
      },
    },
    {
      columnName: 'studyPlanVersionName',
      headerText: table.headers.studyPlanVersionName,
      tdClassName: 'text-center',
      thClassName: 'text-center',
    },
    {
      columnName: 'finalPartialPercentage',
      headerText: table.headers.finalPartialPercentage,
      tdClassName: 'text-center',
      thClassName: 'text-center',
      bold: true,
      cellFormat: ({ value }) => {
        return format(value);
      },
    },
    {
      columnName: 'partial',
      headerText: table.headers.partial,
      tdClassName: 'text-center',
      thClassName: 'text-center',
      cellFormat: ({ row }) => {
        if (row.partial === null) {
          return '-';
        }
        return row.partial;
      },
    },
    {
      columnName: 'partialPassed',
      headerText: table.headers.partialPassed,
      tdClassName: 'text-center',
      thClassName: 'text-center',
      cellFormat: ({ row }) => {
        return (
          <PerformanceAnalysisDetailTablePassed passed={row.partialPassed} />
        );
      },
    },
    {
      columnName: 'finalExamPercentage',
      headerText: table.headers.finalExamPercentage,
      tdClassName: 'text-center',
      thClassName: 'text-center',
      bold: true,
      cellFormat: ({ value }) => {
        return format(value);
      },
    },
    {
      columnName: 'exam',
      headerText: table.headers.exam,
      tdClassName: 'text-center',
      thClassName: 'text-center',
      cellFormat: ({ row }) => {
        if (row.exam === null) {
          return '-';
        }
        return row.exam;
      },
    },
    {
      columnName: 'examPassed',
      headerText: table.headers.examPassed,
      tdClassName: 'text-center',
      thClassName: 'text-center',
      cellFormat: ({ row }) => {
        return <PerformanceAnalysisDetailTablePassed passed={row.examPassed} />;
      },
    },
    {
      columnName: 'final',
      headerText: table.headers.final,
      tdClassName: 'text-center',
      thClassName: 'text-center',
      bold: true,
      cellFormat: ({ row }) => {
        if (row.final === null) {
          return '-';
        }
        return row.final;
      },
    },
    {
      columnName: 'finalPassed',
      headerText: table.headers.finalPassed,
      tdClassName: 'text-center',
      thClassName: 'text-center',
      bold: true,
      cellFormat: ({ row }) => {
        return (
          <PerformanceAnalysisDetailTablePassed passed={row.finalPassed} />
        );
      },
    },
  ];
}
